import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Heart, ArrowRight, Quote } from "lucide-react";
import { Reveal, MaskText, CountUp } from "../components/motion";
import Icon from "../components/Icon";
import { ORG, MISSION, QUOTES, IMPACT, PROGRAMS, FEATURED, MANIFESTO, NEWS, IMAGES } from "../data/site";

const EASE = [0.22, 1, 0.36, 1];

/* ---------------- Hero ---------------- */
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Children of rural Manipur smiling with hope" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/70 via-brand-ink/45 to-brand-ink/85" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 h-full flex flex-col justify-end pb-20">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
          className="text-brand-cream/80 text-sm uppercase tracking-[0.28em] mb-5"
        >
          Bishnupur District · Manipur · Since 2005
        </motion.p>

        <MaskText
          lines={["Everyone Deserves", "a New Life"]}
          className="font-serif text-brand-cream text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight"
          delay={0.35}
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9, ease: EASE }}
          className="mt-7 max-w-xl text-brand-cream/85 text-base sm:text-lg leading-relaxed"
        >
          A grassroots charitable society restoring dignity, health and hope across the ethnic communities of North East India.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.9, ease: EASE }}
          className="mt-9 flex flex-wrap gap-4"
        >
          <Link to="/get-involved#donate" data-testid="hero-donate-btn" className="group inline-flex items-center gap-2 rounded-full bg-brand-accent px-7 py-3.5 text-base font-semibold text-brand-ink hover:bg-[#d95a48] transition-colors">
            <Heart className="h-5 w-5" /> Donate Now
          </Link>
          <Link to="/get-involved" data-testid="hero-getinvolved-btn" className="group inline-flex items-center gap-2 rounded-full border border-brand-cream/40 bg-brand-cream/5 backdrop-blur px-7 py-3.5 text-base font-semibold text-brand-cream hover:bg-brand-cream hover:text-brand-ink transition-colors">
            Get Involved <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
}

/* ---------------- Rotating quote ---------------- */
function QuoteStrip() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % QUOTES.length), 6000);
    return () => clearInterval(t);
  }, []);
  const q = QUOTES[i];
  return (
    <section data-testid="quote-strip" className="bg-brand-teal text-brand-cream">
      <div className="mx-auto max-w-5xl px-5 sm:px-8 py-24 text-center">
        <Quote className="mx-auto h-10 w-10 text-brand-accent" />
        <div className="mt-8 min-h-[160px] sm:min-h-[140px] flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.6, ease: EASE }}
            >
              <p className="font-serif italic text-2xl sm:text-3xl lg:text-4xl leading-snug text-balance">“{q.text}”</p>
              <footer className="mt-7 text-sm uppercase tracking-[0.2em] text-brand-cream/70">
                {q.author} — {q.role}
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>
        <div className="mt-8 flex justify-center gap-2">
          {QUOTES.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setI(idx)}
              data-testid={`quote-dot-${idx}`}
              aria-label={`Quote ${idx + 1}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${idx === i ? "w-8 bg-brand-accent" : "w-2.5 bg-brand-cream/30"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Marquee ---------------- */
function EditorialMarquee() {
  const items = ["Equipping New Life", "Since 2005", "Grassroots Impact", "Dignity for All", "Bishnupur · Manipur"];
  return (
    <div data-testid="marquee" className="border-y border-brand-line bg-brand-cream py-6">
      <Marquee speed={38} gradient={false} autoFill>
        {items.map((t, i) => (
          <span key={i} className="mx-8 inline-flex items-center gap-8 font-serif italic text-3xl sm:text-4xl text-brand-primary/80">
            {t} <span className="h-2 w-2 rounded-full bg-brand-accent" />
          </span>
        ))}
      </Marquee>
    </div>
  );
}

/* ---------------- Current focus ---------------- */
function CurrentFocus() {
  return (
    <section data-testid="current-focus" className="relative overflow-hidden bg-brand-ink text-brand-cream">
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 grid gap-12 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-accent/20 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-brand-accent">
            <span className="h-2 w-2 rounded-full bg-brand-accent animate-pulse" /> Our Current Focus
          </span>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl leading-tight">
            Standing with families affected by conflict & HIV/AIDS
          </h2>
          <p className="mt-6 max-w-xl text-brand-cream/75 leading-relaxed">
            We are providing immediate relief, counselling and long-term support to families displaced by recent conflict and natural disasters in Manipur — alongside our ongoing care for HIV/AIDS-affected households. Your gift reaches them directly.
          </p>
          <Link to="/get-involved#donate" data-testid="focus-donate-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-accent px-7 py-3.5 font-semibold text-brand-ink hover:bg-[#d95a48] transition-colors">
            <Heart className="h-5 w-5" /> Support this initiative
          </Link>
        </Reveal>
        <Reveal delay={0.15} className="relative">
          <div className="overflow-hidden rounded-3xl">
            <img src={IMAGES.relief} alt="Relief and counselling support" className="aspect-[4/3] w-full object-cover" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Impact numbers ---------------- */
function ImpactStrip() {
  return (
    <section data-testid="impact-strip" className="bg-brand-surface">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <Reveal className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.24em] text-brand-primary">Our Impact</span>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-brand-ink">Twenty years of quiet, steady change</h2>
        </Reveal>
        <div className="mt-16 grid gap-y-14 gap-x-8 sm:grid-cols-2 lg:grid-cols-4">
          {IMPACT.map((it, i) => (
            <Reveal key={it.label} delay={i * 0.08}>
              <div className="border-t-2 border-brand-primary pt-5">
                <div className="font-serif text-5xl sm:text-6xl text-brand-primary">
                  <CountUp to={it.value} suffix={it.suffix} />
                </div>
                <p className="mt-3 text-sm text-brand-muted leading-relaxed">{it.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Manifesto ---------------- */
function ManifestoSection() {
  return (
    <section data-testid="manifesto" className="bg-brand-cream">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <Reveal className="max-w-2xl mb-14">
          <span className="text-xs uppercase tracking-[0.24em] text-brand-primary">What We Believe</span>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-brand-ink">Our promise, in four chapters</h2>
        </Reveal>
        <div className="grid gap-px bg-brand-line sm:grid-cols-2 rounded-3xl overflow-hidden border border-brand-line">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.06} className="bg-brand-cream">
              <div className="relative p-8 sm:p-10 h-full">
                <span className="font-serif italic text-7xl text-brand-primary/15 leading-none">{m.n}</span>
                <h3 className="mt-4 font-serif text-2xl text-brand-ink">{m.title}</h3>
                <p className="mt-3 text-brand-muted leading-relaxed">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- What we do (bento) ---------------- */
function WhatWeDo() {
  const cards = PROGRAMS.slice(0, 6);
  const spans = ["sm:col-span-2 sm:row-span-2", "", "", "sm:col-span-2", "", ""];
  return (
    <section data-testid="what-we-do" className="bg-brand-surface">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <Reveal className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.24em] text-brand-primary">What We Do</span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-brand-ink">Programs that equip whole lives</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/what-we-do" data-testid="see-all-programs" className="inline-flex items-center gap-2 rounded-full border border-brand-primary px-6 py-3 text-sm font-semibold text-brand-primary hover:bg-brand-primary hover:text-brand-cream transition-colors">
              View all programs <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>

        <div className="grid gap-5 sm:grid-cols-3 auto-rows-[220px]">
          {cards.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.05} className={spans[i]}>
              <Link to={`/what-we-do#${p.slug}`} data-testid={`program-card-${p.slug}`} className="group relative flex h-full flex-col justify-between overflow-hidden rounded-3xl border border-brand-line bg-brand-cream p-7 transition-colors hover:border-brand-primary">
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <img src={p.image} alt="" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-brand-ink/70" />
                </div>
                <div className="relative z-10 grid place-items-center h-12 w-12 rounded-xl bg-brand-primary/10 text-brand-primary group-hover:bg-brand-accent group-hover:text-brand-ink transition-colors">
                  <Icon name={p.icon} className="h-6 w-6" />
                </div>
                <div className="relative z-10">
                  <h3 className="font-serif text-xl text-brand-ink group-hover:text-brand-cream transition-colors">{p.title}</h3>
                  <p className="mt-2 text-sm text-brand-muted group-hover:text-brand-cream/80 transition-colors line-clamp-2">{p.short}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-primary group-hover:text-brand-accent transition-colors">
                    Learn more <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Featured programs (alternating parallax) ---------------- */
function FeaturedRow({ program, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const reversed = index % 2 === 1;
  return (
    <div ref={ref} className="grid gap-10 lg:grid-cols-2 lg:items-center">
      <Reveal className={reversed ? "lg:order-2" : ""}>
        <div className="relative overflow-hidden rounded-3xl">
          <motion.img style={{ y }} src={program.image} alt={program.title} className="aspect-[4/3] w-full object-cover scale-110" />
        </div>
      </Reveal>
      <Reveal delay={0.1} className={reversed ? "lg:order-1" : ""}>
        <div className="flex items-center gap-3 text-brand-primary">
          <Icon name={program.icon} className="h-6 w-6" />
          <span className="text-xs uppercase tracking-[0.24em]">Featured Program</span>
        </div>
        <h3 className="mt-4 font-serif text-3xl sm:text-4xl text-brand-ink">{program.title}</h3>
        <p className="mt-5 text-brand-muted leading-relaxed">{program.body}</p>
        <Link to={`/what-we-do#${program.slug}`} className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-brand-primary link-underline">
          Discover this program <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
  );
}

function Featured() {
  return (
    <section data-testid="featured-programs" className="bg-brand-cream">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 space-y-24">
        {FEATURED.map((p, i) => (
          <FeaturedRow key={p.slug} program={p} index={i} />
        ))}
      </div>
    </section>
  );
}

/* ---------------- Get involved teaser ---------------- */
function GetInvolvedTeaser() {
  const cards = [
    { t: "Donate", d: "Give directly and transparently via UPI or bank transfer.", to: "/get-involved#donate", tag: "Give" },
    { t: "Partner With Us", d: "Institutional & CSR partnerships that scale local impact.", to: "/get-involved#partner", tag: "Collaborate" },
    { t: "Fundraise", d: "Rally your community to raise funds for a program you love.", to: "/get-involved#fundraise", tag: "Mobilise" },
  ];
  return (
    <section data-testid="get-involved-teaser" className="bg-brand-teal text-brand-cream">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <Reveal className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.24em] text-brand-accent">Get Involved</span>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl">Be part of someone&rsquo;s new life</h2>
        </Reveal>
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {cards.map((c, i) => (
            <Reveal key={c.t} delay={i * 0.08}>
              <Link to={c.to} data-testid={`involve-card-${c.t.toLowerCase().split(" ")[0]}`} className="group flex h-full flex-col justify-between rounded-3xl border border-brand-cream/15 bg-brand-cream/5 p-8 hover:bg-brand-cream/10 transition-colors">
                <span className="text-xs uppercase tracking-[0.2em] text-brand-accent">{c.tag}</span>
                <div className="mt-16">
                  <h3 className="font-serif text-2xl">{c.t}</h3>
                  <p className="mt-3 text-sm text-brand-cream/70 leading-relaxed">{c.d}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-accent">
                    {c.t} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- News ---------------- */
function NewsSection() {
  return (
    <section data-testid="home-news" className="bg-brand-surface">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.24em] text-brand-primary">News & Updates</span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-brand-ink">Stories from the ground</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/news" data-testid="all-news-link" className="inline-flex items-center gap-2 rounded-full border border-brand-primary px-6 py-3 text-sm font-semibold text-brand-primary hover:bg-brand-primary hover:text-brand-cream transition-colors">
              All updates <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {NEWS.map((n, i) => (
            <Reveal key={n.title} delay={i * 0.08}>
              <article data-testid={`news-card-${i}`} className="group overflow-hidden rounded-3xl border border-brand-line bg-brand-cream">
                <div className="overflow-hidden">
                  <img src={n.image} alt={n.title} className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-brand-primary">
                    <span>{n.tag}</span><span className="text-brand-muted">·</span><span className="text-brand-muted">{n.date}</span>
                  </div>
                  <h3 className="mt-3 font-serif text-xl text-brand-ink">{n.title}</h3>
                  <p className="mt-2 text-sm text-brand-muted leading-relaxed">{n.excerpt}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <QuoteStrip />
      <EditorialMarquee />
      <CurrentFocus />
      <ImpactStrip />
      <ManifestoSection />
      <WhatWeDo />
      <Featured />
      <GetInvolvedTeaser />
      <NewsSection />
    </>
  );
}
